import React, { useState } from 'react';
import { VStack, HStack } from 'native-base';
import { Caption, H3 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import { faGem } from '@fortawesome/free-solid-svg-icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import PaginationDot from 'react-native-animated-pagination-dot';
import { useAppNavigate } from '~/pages';
import { i18nFn, useSpringBigStore } from '@springbig/shared';
import useSubscriptionPlansHelpers from '~/pages/subscriptions/hooks/useSubscriptionPlansHelpers';
import PlanBenefits from '~/pages/subscriptions/components/PlanBenefits';
import { format, isBefore } from 'date-fns';
import { useTheme } from '@springbig/shared';
import { Button, Card } from '~/shared/Theme';

type SubscriptionsProps = {
    t: i18nFn;
};

export const Subscriptions = ({ t }: SubscriptionsProps) => {

    const { navigate, params } = useAppNavigate();
    const [activeSlide, setActiveSlide] = useState(0);

    const { highlight_text, primary } = useTheme();
    const { merchant } = useSpringBigStore();

    const { subscribedPlans } = useSubscriptionPlansHelpers();
    const hasSubscription = subscribedPlans && subscribedPlans?.length > 0;

    const generateSchedule = (plan) => {

        let result = '';
        if (!plan.subscription) {
            const billing = {
                both: t('subscriptions.billing.both', [
                    plan.recurring_amount.toFixed(2),
                    plan.yearly_recurring_amount.toFixed(2),
                ]),
                monthly: t('subscriptions.billing.monthly', [
                    plan.recurring_amount.toFixed(2),
                ]),
                yearly: t('subscriptions.billing.yearly', [
                    plan.yearly_recurring_amount.toFixed(2),
                ]),
            };
            const trial =
          plan.trial_period > 0
              ? t('subscriptions.free_then', [plan.trial_period])
              : '';
            result = trial + billing[plan.billing_schedule];
        }
        else if (isBefore(new Date(), new Date(plan.subscription.terminated_at))) {
            const subscription = plan.subscription;
            result = format(new Date(subscription.terminated_at), 'PPP');
        }
        else {
            const subscription = plan.subscription;
            result = format(new Date(subscription.next_charge_on), 'PPP');
        }

        return result;
    };

    return (
        <VStack space={2}>
            <HStack justifyContent={'space-between'} alignItems={'center'}>
                <HStack space={2} alignItems={'center'}>
                    <SpIcon icon={faGem} size={24} color={primary} />
                    <Caption
                        text={'homepage.subscriptions.title'}
                        fontWeight={'bold'}
                    />
                </HStack>
                <Button
                    variant={'text'}
                    modifier={'small'}
                    text={'homepage.subscriptions.viewAll'}
                    onPress={() => navigate(`w/${params.wallet_id}/subscriptions`)}
                />
            </HStack>
            { hasSubscription ? (
                <Swiper
                    spaceBetween={4}
                    onSlideChange={(swiper) => setActiveSlide(swiper.snapIndex)}
                    style={{ width: '100%' }}
                >
                    { subscribedPlans.map((plan, index) => (

                        <SwiperSlide key={index}>
                            <Card
                                alignItems={'center'}
                                justifyContent={'space-between'}
                                py={2}
                                space={2}
                                h={'full'}
                            >
                                <VStack alignItems={'center'}>
                                    <H3 fontWeight={'bold'} fontSize={20}>
                                        { plan.name }
                                    </H3>
                                    <Caption
                                        text={'homepage.subscriptions.premiumMember'}
                                        color={highlight_text}
                                        italic
                                    />
                                </VStack>

                                <HStack
                                    w={'full'}
                                    justifyContent={'space-between'}
                                    alignItems={'center'}
                                    space={4}
                                >
                                    <PlanBenefits plan={plan} />

                                    <Button
                                        onPress={() => {

                                            const id = plan.id.split('-').slice(1, -1).join('-');
                                            const idPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

                                            if (params.wallet_id && idPattern.test(id)) {
                                                navigate(`w/${params.wallet_id}/subscription/${id}`);
                                            }
                                        }}
                                        text={'homepage.subscriptions.managePlan'}
                                    />
                                </HStack>
                                <Caption
                                    opacity={.7}
                                    text={'homepage.subscriptions.planRenews'}
                                    textValues={[generateSchedule(plan)]}
                                    fontSize={'xs'}
                                />
                            </Card>
                        </SwiperSlide>
                    ))}
                </Swiper>
            ) : (
                <Card
                    space={2}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Caption
                        text={'homepage.subscriptions.howToBecome'}
                        fontSize={16}
                        fontWeight={'bold'}
                    />
                    <SpIcon icon={faGem} size={40} color={primary} />
                    <Caption
                        textAlign={'center'}
                        text={'homepage.subscriptions.benefitsDescription'}
                        textValues={[merchant?.name]}
                    />
                    <Button
                        text={'homepage.subscriptions.seeMore'}
                        onPress={() => navigate(`w/${params.wallet_id}/subscriptions`)}
                    />
                </Card>
            )}

            { subscribedPlans?.length > 1 &&
                <VStack alignItems="center">
                    <PaginationDot
                        activeDotColor={primary}
                        curPage={activeSlide}
                        maxPage={subscribedPlans.length}
                    />
                </VStack>
            }
        </VStack>
    );
};
